import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  Sparkles, 
  BrainCircuit, 
  Layout, 
  Smartphone, 
  RefreshCw, 
  Clock, 
  Users, 
  ArrowRight,
  ChevronRight,
  Search,
  Filter,
  Download,
  MoreVertical,
  CheckCircle2,
  Code2,
  Truck,
  ShoppingBag,
  Gift,
  Copy,
  DollarSign,
  TrendingUp,
  Activity,
  Target,
  Send,
  Percent
} from 'lucide-react';
import './Offers.css';
import '../offer page/Offers.css'


const Offers = () => {
  const [activeMainTab, setActiveMainTab] = useState('Offer Generator');
  const [activeBuilderTab, setActiveBuilderTab] = useState('Strategy');
  const [discountType, setDiscountType] = useState('Percentage');
  const [valueAmount, setValueAmount] = useState(15);
  const [optimization, setOptimization] = useState('Conversion Rate');
  const [headline, setHeadline] = useState('Here is 15% Off!');
  const [description, setDescription] = useState('We noticed you eyeing those items. Use this code at checkout to save on your order.');
  const [buttonText, setButtonText] = useState('Apply Discount');
  const [promoCode, setPromoCode] = useState('SAVE15');
  const [accentColor, setAccentColor] = useState('#8B5CF6');
  const [triggers, setTriggers] = useState({ timeOnSite: true, exitIntent: false });
  const [targetSegments, setTargetSegments] = useState(['Returning Visitor']);
  const [isLaunched, setIsLaunched] = useState(false);
  const [decisionLog, setDecisionLog] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (!isLaunched) {
      if (discountType === 'Percentage') setHeadline(`Here is ${valueAmount}% Off!`);
      else if (discountType === 'Fixed Amount') setHeadline(`Save $${valueAmount} Today!`);
      else setHeadline('Free Shipping Applied!');
    }
  }, [discountType, valueAmount, isLaunched]);

  useEffect(() => {
    if (activeMainTab === 'Dynamic Offer Brain') {
      const events = [
        "Visitor viewed 'Winter Glow' collection",
        "Cart abandonment detected: $142.50",
        "High intent signal: 3 sessions in 24h",
        "Returning customer identified (VIP Segment)",
        "Price sensitivity check: Low threshold match",
      ];
      const offers = ["SAVE15", "FREE-SHIP", "VIP-GIFT", "BUNDLE-UP"];

      const interval = setInterval(() => {
        const newEntry = {
          id: Math.random().toString(36).substr(2, 9),
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
          event: events[Math.floor(Math.random() * events.length)],
          match: offers[Math.floor(Math.random() * offers.length)],
          probability: 70 + Math.floor(Math.random() * 28),
          status: Math.random() > 0.3 ? 'Deployed' : 'Evaluating'
        };
        setDecisionLog(prev => [newEntry, ...prev].slice(0, 10));
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [activeMainTab]);

  const generateCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < 6; i++) result += chars.charAt(Math.floor(Math.random() * chars.length));
    setPromoCode(result);
  };

  const handleLaunch = () => {
    setIsLaunched(true);
    setTimeout(() => {
      setActiveMainTab('Dynamic Offer Brain');
      setIsLaunched(false);
    }, 1500);
  };

  const renderGenerator = () => (
    <div className="row g-4">
      <div className="col-12 col-lg-8">
        {/* Builder Tabs */}
        <div className="builder-tabs-wrapper">
          {['Strategy', 'Creative', 'Logic'].map(tab => {
            const isActive = activeBuilderTab === tab;
            let iconComponent = null;
            if (tab === 'Strategy') iconComponent = <BrainCircuit size={18} />;
            if (tab === 'Creative') iconComponent = <Layout size={18} />;
            if (tab === 'Logic') iconComponent = <Code2 size={18} />;

            return (
              <button
                key={tab}
                onClick={() => setActiveBuilderTab(tab)}
                className={`builder-tab-btn ${isActive ? 'active' : ''} builder-tab-${tab.toLowerCase()}`}
              >
                {iconComponent}
                <span>{tab}</span>
              </button>
            );
          })}
        </div>

        {/* Builder Content */}
        <div className="builder-content-card">
          {isLaunched && (
            <div className="launch-overlay">
              <div className="launch-icon-wrapper">
                <CheckCircle2 size={32} />
              </div>
              <h3 className="launch-title">Offer Intelligence Active</h3>
              <p className="launch-subtitle">Deploying to Dynamic Offer Brain...</p>
            </div>
          )}

          {activeBuilderTab === 'Strategy' && (
            <div className="builder-section-content">
              {/* Discount Type Section */}
              <section className="builder-section">
                <h4 className="builder-section-title">Discount Type</h4>
                <div className="row g-3">
                  {[
                    { id: 'Percentage', icon: <Percent size={24} strokeWidth={3} />, sub: 'Fixed % off' },
                    { id: 'Fixed Amount', icon: <DollarSign size={24} strokeWidth={3} />, sub: 'Hard $ value' },
                    { id: 'Free Ship', icon: <Truck size={24} strokeWidth={2.5} />, sub: 'Remove ship cost' }
                  ].map(type => (
                    <div key={type.id} className="col-12 col-md-4">
                      <button
                        onClick={() => setDiscountType(type.id)}
                        className={`discount-type-card ${discountType === type.id ? 'active' : ''}`}
                      >
                        <div className="discount-type-icon">{type.icon}</div>
                        <div className="discount-type-info">
                          <p className="discount-type-name">{type.id}</p>
                          <p className="discount-type-sub">{type.sub}</p>
                        </div>
                      </button>
                    </div>
                  ))}
                </div>
              </section>

              {/* Goal Optimization */}
              <section className="builder-section">
                <h4 className="builder-section-title">Goal Optimization</h4>
                <div className="optimization-pills">
                  {['Conversion Rate', 'AOV Growth', 'Clear Inventory'].map(opt => (
                    <button
                      key={opt}
                      onClick={() => setOptimization(opt)}
                      className={`optimization-pill ${optimization === opt ? 'active' : ''}`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </section>

              {/* Global Performance Metrics */}
              <section className="builder-section performance-section">
                <h4 className="builder-section-title">Global Performance Metrics</h4>
                <div className="performance-metric-card">
                  <div className="performance-metric-left">
                    <div className="performance-metric-icon">
                      <Send size={24} />
                    </div>
                    <div className="performance-metric-info">
                      <p className="performance-metric-label">Total Number of offers sent in 30 days</p>
                      <h3 className="performance-metric-value">
                        12,842 <span className="performance-metric-trend">+14.2%</span>
                      </h3>
                    </div>
                  </div>
                  <div className="performance-chart">
                    {[35, 45, 65, 55, 85, 70, 95].map((h, i) => (
                      <div 
                        key={i} 
                        className={`performance-bar ${i === 6 ? 'active' : ''}`}
                        style={{ height: `${h}%` }}
                      ></div>
                    ))}
                  </div>
                </div>
              </section>
            </div>
          )}

          {activeBuilderTab === 'Creative' && (
            <div className="builder-section-content creative-content">
              <div className="row g-4">
                <div className="col-12 col-md-6">
                  <div className="form-group-custom">
                    <label className="form-label-custom">Headline</label>
                    <input 
                      type="text" 
                      value={headline} 
                      onChange={e => setHeadline(e.target.value)} 
                      className="form-control-custom"
                    />
                  </div>
                </div>
                <div className="col-12 col-md-6">
                  <div className="form-group-custom">
                    <label className="form-label-custom">Button Text</label>
                    <input 
                      type="text" 
                      value={buttonText} 
                      onChange={e => setButtonText(e.target.value)} 
                      className="form-control-custom"
                    />
                  </div>
                </div>
                <div className="col-12">
                  <div className="form-group-custom">
                    <label className="form-label-custom">Description Body</label>
                    <textarea 
                      rows="3" 
                      value={description} 
                      onChange={e => setDescription(e.target.value)} 
                      className="form-control-custom"
                    ></textarea>
                  </div>
                </div>
                <div className="col-12 col-md-6">
                  <div className="form-group-custom">
                    <label className="form-label-custom">Offer Code</label>
                    <div className="promo-code-input-wrapper">
                      <input 
                        type="text" 
                        value={promoCode} 
                        onChange={e => setPromoCode(e.target.value)} 
                        className="form-control-custom promo-code-input"
                      />
                      <button onClick={generateCode} className="promo-code-refresh">
                        <RefreshCw size={18} />
                      </button>
                    </div>
                  </div>
                </div>
                <div className="col-12 col-md-6">
                  <div className="form-group-custom">
                    <label className="form-label-custom">Brand Accent</label>
                    <div className="color-picker-wrapper">
                      {['#8B5CF6', '#3B82F6', '#10B981', '#F43F5E', '#F59E0B'].map(c => (
                        <button 
                          key={c} 
                          onClick={() => setAccentColor(c)} 
                          className={`color-picker-swatch ${accentColor === c ? 'active' : ''}`}
                          style={{ backgroundColor: c }}
                        ></button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeBuilderTab === 'Logic' && (
            <div className="builder-section-content logic-content">
              <section className="builder-section">
                <h4 className="builder-section-title">Trigger Conditions</h4>
                <div className="trigger-list">
                  {[
                    { id: 'timeOnSite', label: 'Time on Site', sub: 'Wait 30s before showing', icon: <Clock size={18} /> },
                    { id: 'exitIntent', label: 'Exit Intent', sub: 'Show on mouse exit', icon: <Target size={18} /> }
                  ].map(item => (
                    <div key={item.id} className="trigger-item">
                      <div className="trigger-item-left">
                        <div className="trigger-icon-wrapper">{item.icon}</div>
                        <div className="trigger-info">
                          <p className="trigger-label">{item.label}</p>
                          <p className="trigger-sub">{item.sub}</p>
                        </div>
                      </div>
                      <button 
                        onClick={() => setTriggers(prev => ({ ...prev, [item.id]: !prev[item.id] }))}
                        className={`toggle-switch ${triggers[item.id] ? 'active' : ''}`}
                      >
                        <div className="toggle-switch-knob"></div>
                      </button>
                    </div>
                  ))}
                </div>
              </section>

              <div className="launch-button-wrapper">
                <button onClick={handleLaunch} className="launch-engine-btn">
                  Launch Engine <ArrowRight size={20} strokeWidth={2.5} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="col-12 col-lg-4">
        <div className="preview-panel">
          <div className="preview-panel-header">
            <div className="preview-header-left">
              <Smartphone size={20} />
              <h3 className="preview-title">Deployment Preview</h3>
            </div>
            <div className="preview-dots">
              <span className="preview-dot red"></span>
              <span className="preview-dot yellow"></span>
              <span className="preview-dot green"></span>
            </div>
          </div>

          <div className="preview-display-area">
            <div className="preview-phone-mockup">
              <div className="preview-phone-header">
                <div className="preview-phone-icon">
                  <Gift size={26} strokeWidth={2.5} />
                </div>
                <span 
                  className="preview-phone-badge"
                  style={{ 
                    borderColor: `${accentColor}30`, 
                    color: accentColor, 
                    backgroundColor: `${accentColor}10` 
                  }}
                >
                  {targetSegments.length > 0 ? 'Dynamic' : 'Limited'}
                </span>
              </div>
              <h4 className="preview-phone-headline">{headline}</h4>
              <p className="preview-phone-desc">{description}</p>
              <div className="preview-phone-code">
                <span className="preview-phone-code-text">{promoCode}</span>
                <Copy size={16} />
              </div>
              <button 
                className="preview-phone-cta"
                style={{ 
                  backgroundColor: accentColor, 
                  boxShadow: `0 10px 25px -5px ${accentColor}40` 
                }}
              >
                {buttonText}
              </button>
            </div>
          </div>

          <div className="preview-recommendation">
            <div className="preview-rec-header">
              <Sparkles size={18} />
              <span>Neural Recommendation</span>
            </div>
            <p className="preview-rec-text">
              {triggers.exitIntent ? "System armed for Exit Intent trigger." : "Timed deployment active (30s delay)."} 
              Expected CR uplift: <span className="preview-rec-highlight">+14.2%</span>.
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderBrain = () => (
    <div className="brain-container">
      {/* Metrics Row */}
      <div className="row g-4 mb-4">
        {[
          { label: 'AI Confidence Score', val: '98.2%', delta: '+0.5%', icon: <Sparkles />, color: 'purple' },
          { label: 'Real-Time Signals', val: '142', sub: '/min', delta: 'Active', icon: <Activity />, color: 'blue' },
          { label: 'Conversion Uplift', val: '+12.4%', sub: 'vs Control', delta: '+2.1%', icon: <TrendingUp />, color: 'emerald' }
        ].map((m, i) => (
          <div key={i} className="col-12 col-md-4">
            <div className={`brain-metric-card brain-metric-${m.color}`}>
              <div className="brain-metric-header">
                <div className="brain-metric-icon">{m.icon}</div>
                <span className={`brain-metric-badge badge-${m.delta === 'Active' ? 'blue' : 'emerald'}`}>
                  {m.delta}
                </span>
              </div>
              <div className="brain-metric-body">
                <p className="brain-metric-label">{m.label}</p>
                <h3 className="brain-metric-value">
                  {m.val} {m.sub && <span className="brain-metric-sub">{m.sub}</span>}
                </h3>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="row g-4">
        <div className="col-12 col-lg-5">
          <h4 className="section-subtitle">Active Logic Modules</h4>
          <div className="logic-modules-list">
            {[
              { id: 'cart', label: 'Cart Abandonment', status: 'Running', icon: <ShoppingBag />, impact: 'High' },
              { id: 'price', label: 'Price Sensitivity', status: 'Running', icon: <Target />, impact: 'Medium' },
              { id: 'inv', label: 'Inventory Clearance', status: 'Learning', icon: <RefreshCw />, impact: 'Critical' },
              { id: 'weather', label: 'Weather Context', status: 'Paused', icon: <Activity />, impact: 'Low' }
            ].map(mod => (
              <div key={mod.id} className="logic-module-item">
                <div className="logic-module-left">
                  <div className="logic-module-icon">{mod.icon}</div>
                  <div className="logic-module-info">
                    <p className="logic-module-name">{mod.label}</p>
                    <div className="logic-module-status">
                      <span className={`status-dot status-${mod.status.toLowerCase()}`}></span>
                      <span className="status-text">{mod.status}</span>
                    </div>
                  </div>
                </div>
                <div className="logic-module-impact">
                  <p className="impact-label">Impact</p>
                  <p className={`impact-value ${mod.impact === 'High' || mod.impact === 'Critical' ? 'high' : 'medium'}`}>
                    {mod.impact}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="col-12 col-lg-7">
          <div className="decision-stream-header">
            <h4 className="section-subtitle">Live Decision Stream</h4>
            <div className="live-indicator">
              <span className="live-pulse"></span>
              <span className="live-text">Intelligence Node Active</span>
            </div>
          </div>
          <div className="decision-stream-container">
            {decisionLog.map(entry => (
              <div key={entry.id} className="decision-entry">
                <div className="decision-entry-left">
                  <div className="decision-timestamp">{entry.timestamp}</div>
                  <div className="decision-details">
                    <p className="decision-event">{entry.event}</p>
                    <p className="decision-flow">
                      Logic Flow: Neural Match <ChevronRight size={10} className="chevron-icon" /> 
                      <span className="decision-match">{entry.match}</span>
                    </p>
                  </div>
                </div>
                <div className="decision-entry-right">
                  <div className="decision-confidence">
                    <p className="confidence-label">Confidence</p>
                    <p className="confidence-value">{entry.probability}%</p>
                  </div>
                  <span className={`decision-status ${entry.status === 'Deployed' ? 'deployed' : 'evaluating'}`}>
                    {entry.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderHistory = () => (
    <div className="history-container">
      <div className="history-controls">
        <div className="history-search-wrapper">
          <Search className="search-icon-left" size={20} />
          <input 
            type="text" 
            placeholder="Search generated offers..." 
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="history-search-input"
          />
        </div>
        <div className="history-actions">
          <button className="history-action-btn filter-btn">
            <Filter size={18} /> Advanced Filter
          </button>
          <button className="history-action-btn export-btn">
            <Download size={18} /> Export Analytics
          </button>
        </div>
      </div>

      <div className="history-table-wrapper">
        <table className="history-table">
          <thead>
            <tr>
              <th>Offer Asset & Logic</th>
              <th>Deployment Logic</th>
              <th>Date Launched</th>
              <th className="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            {[
              { name: 'Black Friday VIP', code: 'BFCM-20', logic: 'Exit Intent > $200', date: 'Nov 12, 2024', revenue: '$42,120' },
              { name: 'Welcome Gift', code: 'HELLO-10', logic: 'New Session', date: 'Oct 28, 2024', revenue: '$18,450' },
              { name: 'Cart Recovery Elite', code: 'RECOVER-15', logic: 'Cart Abandon > 1h', date: 'Oct 15, 2024', revenue: '$26,900' },
            ].filter(o => o.name.toLowerCase().includes(searchTerm.toLowerCase())).map((offer, i) => (
              <tr key={i} className="history-row">
                <td>
                  <div className="offer-cell">
                    <div className="offer-cell-icon">
                      <Gift size={24} strokeWidth={2.5} />
                    </div>
                    <div className="offer-cell-info">
                      <p className="offer-cell-name">{offer.name}</p>
                      <div className="offer-cell-meta">
                        <span className="offer-code-badge">{offer.code}</span>
                        <span className="offer-revenue">{offer.revenue} Rev</span>
                      </div>
                    </div>
                  </div>
                </td>
                <td className="logic-cell">{offer.logic}</td>
                <td className="date-cell">{offer.date}</td>
                <td className="text-end">
                  <button className="actions-btn">
                    <MoreVertical size={20} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <>
      <Helmet>
        <title>Offer Engine - Revexia AI Revenue Brain</title>
        <meta name="description" content="AI-powered offer generation and dynamic decision engine" />
      </Helmet>

      <div className="offers-page-wrapper">
        <div className="container-fluid px-4">
          {/* Page Header */}
          <div className="offers-page-header">
            <div className="offers-header-info">
              <h2 className="offers-page-title">Campaign Hub</h2>
              <p className="offers-page-subtitle">Configure neural decision logic for real-time engagement</p>
            </div>
            <div className="main-tabs-wrapper">
              {['Offer Generator', 'Dynamic Offer Brain', 'History'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveMainTab(tab)}
                  className={`main-tab-btn ${activeMainTab === tab ? 'active' : ''}`}
                >
                  {tab}
                </button>
              ))}
            </div>
          </div>

          {/* Main Content */}
          <div className="offers-main-content">
            {activeMainTab === 'Offer Generator' && renderGenerator()}
            {activeMainTab === 'Dynamic Offer Brain' && renderBrain()}
            {activeMainTab === 'History' && renderHistory()}
          </div>

          {/* Global Intelligence Footer */}
          <div className="global-intelligence-footer">
            <div className="intelligence-footer-card">
              <div className="intelligence-footer-left">
                <div className="intelligence-footer-icon">
                  <BrainCircuit size={32} strokeWidth={2.5} />
                </div>
                <div className="intelligence-footer-info">
                  <h4 className="intelligence-footer-title">Global Intelligence Pulse</h4>
                  <p className="intelligence-footer-text">
                    Neural nodes are currently prioritizing <span className="highlight-text">"Percentage Off"</span> triggers for high-intent visitors based on a 22.4% historical conversion uplift in the current window.
                  </p>
                </div>
              </div>
              <div className="intelligence-footer-right">
                <p className="capacity-label">Network Capacity</p>
                <div className="capacity-display">
                  <span className="capacity-value">99.8%</span>
                  <div className="capacity-bar-track">
                    <div className="capacity-bar-fill" style={{ width: '99.8%' }}></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Offers;
